import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService, Kindergarten, LinksService, User } from '@isophi/core-legacy';
import { EpErrorService, ToastService } from '@isophi/core-ui';
import { TeachersService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, finalize, map, of, Subscription, switchMap } from 'rxjs';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { UserService } from '../../../../core/services/user.service';
import {
  getGroupsFromUIRole,
  getUIRoleFromGroups,
  USER_ROLE_OPTIONS,
  UserRoleOption,
  UserRoleType,
} from '../../../user/constants/teacher-roles.constants';
import { UserToAdd } from '../../models/user-to-add.model';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditUserComponent implements AfterViewInit, OnDestroy {
  private readonly linksService = inject(LinksService);
  private readonly epErrorService = inject(EpErrorService);

  protected disabledButton = false;

  protected formError: string | null = null;

  protected firstNameError: string | null = null;

  protected lastNameError: string | null = null;

  protected emailError: string | null = null;

  protected passwordError: string | null = null;

  protected passwordConfirmError: string | null = null;

  protected setGroupsError: string | null = null;

  protected userToAdd: UserToAdd = new UserToAdd();

  protected userId: number;

  protected roleOptions: UserRoleOption[] = USER_ROLE_OPTIONS;

  protected isDirector = false;
  protected workplaces: Kindergarten[] = [];
  protected selectedWorkplaceUuid: string | null = null;
  protected originalWorkplaceUuid: string | null = null;
  protected isEditedUserDirectorOnLoad = false;

  protected readonly forgottenPasswordLink = this.linksService.getTeacherAppUrl({
    redirect: '/login/forgotten-password',
    withLogin: false,
  });

  user: User;

  private _subs: Subscription[] = [];

  public constructor(
    public links: RouterLinkFactory,
    private spinner: NgxSpinnerService,
    private toastr: ToastService,
    private cdRef: ChangeDetectorRef,
    private helperService: HelperService,
    private translocoService: TranslocoService,
    private userService: UserService,
    private teachersService: TeachersService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {
    this.helperService.setTitle(null, this.translocoService.translate('user/edit'));
    this.user = this.authService.loggedUser;
    this.isDirector = this.user?.teacher?.isDirector() || false;
    this.workplaces = this.user?.teacher?.kindergartens || [];
  }

  ngAfterViewInit(): void {
    this._subs.push(
      this.route.paramMap.subscribe((params) => {
        const id = params.get('id');
        this.userId = Number.parseInt(id, 10);

        this.userService.getUser(this.userId).subscribe((userData) => {
          this.userToAdd = new UserToAdd(userData.firstName, userData.lastName, userData.email, '');
          // Set the current user roles/groups
          this.userToAdd.setGroups = userData.teacher?.groups || [];
          // Determine UI role from groups
          this.userToAdd.selectedRole = getUIRoleFromGroups(this.userToAdd.setGroups);
          this.isEditedUserDirectorOnLoad = this.userToAdd.selectedRole === UserRoleType.DIRECTOR;
          this.originalWorkplaceUuid = this.userService.getCurrentKindergartenUUID();
          this.selectedWorkplaceUuid = this.originalWorkplaceUuid;
          this.cdRef.markForCheck();
        });
      })
    );
  }

  ngOnDestroy(): void {
    this._subs.forEach((_sub) => _sub.unsubscribe());
  }

  /**
   * Handle role selection change
   */
  protected onRoleChange(): void {
    this.userToAdd.setGroups = getGroupsFromUIRole(this.userToAdd.selectedRole);
  }

  protected canTransferWorkplace(): boolean {
    return this.isDirector && this.workplaces.length > 1;
  }

  /**
   * Function to process add user form, when submitted.
   */
  public onSubmit(): void {
    this.startProcessingForm();

    // Ensure setGroups is updated based on selected role
    this.userToAdd.setGroups = getGroupsFromUIRole(this.userToAdd.selectedRole);
    const shouldTransfer =
      this.selectedWorkplaceUuid && this.originalWorkplaceUuid && this.selectedWorkplaceUuid !== this.originalWorkplaceUuid;

    if (!this.userToAdd.checkPasswordConfirm()) {
      this.passwordConfirmError = this.translocoService.translate('user/newPasswordConfirmError');
      this.finishProcessingForm();
      return;
    }

    type TransferResult = { transferDone: true; workplaceName?: string } | { transferDone: false } | { transferFailed: true };

    this.userService
      .editUser(this.userToAdd, this.userId)
      .pipe(
        switchMap(() => {
          if (shouldTransfer) {
            return this.teachersService.teachersTransferToWorkplaceCreate({ workplaceUuid: this.selectedWorkplaceUuid }, this.userId).pipe(
              map((response) => ({ transferDone: true as const, workplaceName: response?.workplaceName })),
              catchError(() => {
                this.toastr.error(this.translocoService.translate('transfer/userMovedError'));
                return of({ transferFailed: true as const });
              })
            );
          }
          return of({ transferDone: false as const });
        }),
        finalize(() => this.finishProcessingForm())
      )
      .subscribe({
        next: (result: TransferResult) => {
          if ('transferFailed' in result && result.transferFailed) {
            return;
          }
          this.userToAdd.clear();
          if ('transferDone' in result && result.transferDone) {
            this.toastr.success(this.translocoService.translate('transfer/userMovedSuccess', { workplace: result.workplaceName }));
          } else {
            this.toastr.success(this.translocoService.translate('user/userEditSuccessToast'));
          }
          this.router.navigate(this.links.userList());
        },
        error: (error) => {
          const errorData = error.error;

          if (errorData.errors === undefined || error.status === 403) {
            this.formError = this.translocoService.translate('user/userEditErrorToast');
            this.epErrorService.handleEpErrorResponse(error, { defaultMessage: this.formError });
            return;
          }

          for (const errorItem of errorData.errors) {
            if (errorItem.field === 'firstName') {
              this.firstNameError = errorItem.message;
            }
            if (errorItem.field === 'lastName') {
              this.lastNameError = errorItem.message;
            }
            if (errorItem.field === 'email') {
              this.emailError = errorItem.message;
            }
            if (errorItem.field === 'password') {
              this.passwordError = errorItem.message;
            }
            if (errorItem.field === 'passwordConfirm') {
              this.passwordConfirmError = errorItem.message;
            }
            if (errorItem.field === 'set_groups') {
              this.setGroupsError = errorItem.message;
            }
          }
          this.cdRef.detectChanges();
        },
      });
  }

  /**
   * Start processing form.
   */
  protected startProcessingForm(): void {
    this.disabledButton = true;
    this.clearFormErrors();
    this.spinner.show();
  }

  /**
   * Finish processing form.
   */
  protected finishProcessingForm(): void {
    this.spinner.hide();
    this.disabledButton = false;
  }

  /**
   * Clear all errors in form.
   */
  protected clearFormErrors(): void {
    this.formError = null;
    this.firstNameError = null;
    this.lastNameError = null;
    this.emailError = null;
    this.passwordError = null;
    this.passwordConfirmError = null;
    this.setGroupsError = null;
  }
}
